// client/src/components/layout/EnhancedSidebar.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo, type ElementType, type ReactNode } from 'react';
import ReactDOM from 'react-dom';
import AppLink from '../common/AppLink';
import {
  FaSignature,
  FaTable,
  FaEdit,
  FaFilePdf,
  FaChartLine,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaCog,
  FaExclamationTriangle,
  FaEnvelope,
  FaMapMarkerAlt,
  FaRandom,
  FaDatabase,
  FaTelegram,
  FaClipboardList,
  FaTruck,
  FaTimes,
  FaSyncAlt,
  FaBrain,
  FaClipboardCheck,
  FaChartBar,
  FaTicketAlt,
  FaBalanceScale,
  FaBox,
  FaSearch,
  FaCalendarAlt,
  FaTruckMoving,
  FaWrench,
  FaRoute,
  // LexiStar assurance (ST-00188 M3-U1)
  FaTasks,
  FaBullhorn,
  FaHistory,
  FaCertificate,
  // LexiStar operations (ST-00188 M3-U2)
  FaMapMarkedAlt,
  FaPlayCircle,
  FaIdCard,
  FaComments,
  // LexiStar reports (ST-00188 M4-R2)
  FaFileAlt,
  FaBullseye,
  // LexiStar numbers (ST-00188 M4-D)
  FaTachometerAlt,
  FaHourglassHalf,
  // LexiStar usage & billing (ST-00207 M4b-A)
  FaFileInvoiceDollar,
  // LexiStar Live View (ST-00210), notification setup (ST-00252), the inbox (ST-00251)
  FaSatelliteDish,
  FaBell,
  // Help & training (ST-00200)
  FaQuestionCircle,
  // Routes, trucks & drivers (ST-00254/255/256)
  FaSortAmountDown,
  FaThLarge,
  FaListUl,
} from 'react-icons/fa';
import { Monitor, Sun, Moon, type LucideIcon } from 'lucide-react';
import { useAuth, useTheme, type ThemeMode } from '../../types/commonComponents';
import { getRequiredPermission, permits } from '../../config/pagePermissions';
import { SUPPORT_APP_PATH } from '../../services/supportPlatform';
import useSupportUnreadCount from '../../hooks/useSupportUnreadCount';
import { useLexiStarUnread } from '../common/lexistarNotificationStream';
import './EnhancedSidebar.css';

// Theme cycle: Detect → Light → Dark → Detect.
const THEME_CYCLE: Record<ThemeMode, ThemeMode> = { detect: 'light', light: 'dark', dark: 'detect' };
const THEME_META: Record<ThemeMode, { icon: LucideIcon; label: string }> = {
  detect: { icon: Monitor, label: 'Detect' },
  light: { icon: Sun, label: 'Light' },
  dark: { icon: Moon, label: 'Dark' },
};

/** One sidebar entry. `external` entries leave the SPA (a real anchor to `href`). */
export interface SidebarItem {
  path: string;
  label: string;
  icon: ReactNode;
  /** Draw a divider under the entry. */
  divider?: boolean;
  /**
   * Section the entry belongs to. A small heading is drawn above the first visible entry of
   * each section (a divider when the sidebar is collapsed), so permission-hidden entries never
   * leave an orphan heading behind.
   */
  section?: string;
  external?: boolean;
  href?: string;
  /** Unread count shown as a badge (hidden at 0). */
  badge?: number;
}

export interface EnhancedSidebarProps {
  /** Controlled collapse state; uncontrolled (defaultCollapsed) when undefined. */
  isCollapsed?: boolean;
  onToggle?: () => void;
  defaultCollapsed?: boolean;
  className?: string;
  /**
   * Passed by the shell; not read — visibility comes from AuthContext's
   * hasPermission() through config/pagePermissions.
   */
  userPermissions?: string[];
  /**
   * Supplied by the shell: the router's path untabbed, the active tab's path
   * tabbed. The sidebar renders outside every tab router, so it can't read it.
   */
  activePathname?: string;
}

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
};

// Tooltip portal for collapsed desktop state
/**
 * The navigation, grouped the way the work is done: the weighbridge core first, amendments,
 * then LexiStar in its three modes (operate the day → analyse → assure and configure), tools,
 * Help & training, and support/settings last. Order within a section follows the daily
 * workflow, not the build order. Keep `pagePermissions` in step when adding an entry.
 * `unreadNotificationCount` (ST-00251) is the LexiStar Notifications badge, as Support's.
 */
export function buildMenuItems(unreadTicketCount: number, unreadNotificationCount = 0): SidebarItem[] {
  const WB = 'Weighbridge';
  const AM = 'Amendments';
  const OP = 'LexiStar · Operate';
  const AN = 'LexiStar · Analyse';
  const AS = 'LexiStar · Assure & configure';
  const TL = 'Tools';
  const HP = 'Help';
  return [
    { section: WB, path: '/', label: 'Operations Overview', icon: <FaChartLine /> },
    { section: WB, path: '/route-summary', label: 'Route Summary', icon: <FaTable /> },
    { section: WB, path: '/product-movement', label: 'Product Movement', icon: <FaBox /> },
    { section: WB, path: '/period-summary', label: 'Period Summary', icon: <FaCalendarAlt /> },
    { section: WB, path: '/location-analytics', label: 'Location Analytics', icon: <FaMapMarkerAlt /> },
    { section: WB, path: '/transaction-search', label: 'Transaction Search', icon: <FaSearch /> },
    // ST-00242: a truck's weighbridge trips, so it sits with the weighbridge pages, next to
    // Transaction Search (it used to close LexiStar · Operate, but it is not LexiStar).
    { section: WB, path: '/vehicle-tracker', label: 'Vehicle Tracker', icon: <FaTruck /> },
    { section: WB, path: '/custom-summaries', label: 'Custom Summaries', icon: <FaDatabase /> },
    { section: WB, path: '/order-management', label: 'Order Management', icon: <FaClipboardList /> },

    { section: AM, path: '/amendments-allocate-transactions', label: 'Amendments', icon: <FaEdit /> },
    { section: AM, path: '/amendment-approvals', label: 'Amendment Approvals', icon: <FaClipboardCheck /> },
    { section: AM, path: '/amendment-analytics', label: 'Amendment Analytics', icon: <FaChartBar /> },
    { section: AM, path: '/transaction-allocation', label: 'Transaction Allocation', icon: <FaRandom /> },
    { section: AM, path: '/signatures', label: 'Signatures', icon: <FaSignature /> },

    // ST-00210: the live map, out of Geofences into its own page.
    { section: OP, path: '/lexistar/live', label: 'Live View', icon: <FaSatelliteDish /> },
    { section: OP, path: '/lexistar/replay', label: 'Replay', icon: <FaPlayCircle /> },
    { section: OP, path: '/lexistar/dashboard', label: 'Dashboard', icon: <FaTachometerAlt /> },
    { section: OP, path: '/lexistar/status-board', label: 'Status Board', icon: <FaTruckMoving /> },
    // ST-00249: maintenance bookings, breakdowns and delays per truck (the time model).
    { section: OP, path: '/lexistar/maintenance', label: 'Maintenance', icon: <FaWrench /> },
    { section: OP, path: '/lexistar/plan-board', label: 'Plan Board', icon: <FaMapMarkedAlt /> },
    { section: OP, path: '/lexistar/plan', label: 'Plan & Dispatch', icon: <FaClipboardCheck /> },
    // ST-00250: the dispatch planning board — order bubbles, trucks dragged in and out, absorption.
    { section: OP, path: '/lexistar/dispatch-board', label: 'Dispatch Board', icon: <FaThLarge /> },
    // ST-00318: the dispatched trips over a period (until then reached only from a KPI figure).
    { section: OP, path: '/lexistar/trips', label: 'Trip list', icon: <FaListUl /> },
    { section: OP, path: '/lexistar/approvals', label: 'Deviation Approvals', icon: <FaRoute /> },
    { section: OP, path: '/lexistar/review', label: 'Review Queue', icon: <FaTasks /> },
    // ST-00251: the user's own notifications, filterable, with the unread badge.
    {
      section: OP,
      path: '/lexistar/notifications',
      label: 'Notifications',
      icon: <FaBell />,
      badge: unreadNotificationCount,
    },
    { section: OP, path: '/lexistar/messages', label: 'Messages', icon: <FaComments /> },
    { section: OP, path: '/lexistar/drivers', label: 'Drivers', icon: <FaIdCard /> },

    { section: AN, path: '/lexistar/kpis', label: 'KPIs', icon: <FaChartBar /> },
    // ST-00254/255/256: fuel vs allowance, tonnes and speeding ranked by route, truck or driver.
    { section: AN, path: '/lexistar/routes-trucks-drivers', label: 'Routes, trucks & drivers', icon: <FaSortAmountDown /> },
    { section: AN, path: '/lexistar/dwell', label: 'Dwell', icon: <FaHourglassHalf /> },
    // ST-00247 / ST-00248: availability and utilisation per truck, targets and actual vs target.
    { section: AN, path: '/lexistar/fleet-performance', label: 'Fleet & targets', icon: <FaBullseye /> },
    { section: AN, path: '/lexistar/reports', label: 'Reports', icon: <FaFileAlt /> },

    { section: AS, path: '/geofencing', label: 'Geofences', icon: <FaMapMarkerAlt /> },
    { section: AS, path: '/lexistar/declarations', label: 'Declarations', icon: <FaBullhorn /> },
    { section: AS, path: '/lexistar/recompute-jobs', label: 'Recompute Jobs', icon: <FaHistory /> },
    { section: AS, path: '/lexistar/commissioning', label: 'Commissioning', icon: <FaCertificate /> },
    // ST-00252 (2026-10-29): Notification setup is no longer here. The sidebar keeps the work;
    // setup lives in Settings → LexiStar → Notification setup.
    // ST-00240: moved here from Analyse (M4b-A) — also in Settings → LexiStar.
    { section: AS, path: '/lexistar/billing', label: 'Usage & billing', icon: <FaFileInvoiceDollar /> },

    { section: TL, path: '/email-reports', label: 'Email Reports', icon: <FaEnvelope /> },
    { section: TL, path: '/pdf-generator-v2', label: 'PDF Generator', icon: <FaFilePdf /> },
    { section: TL, path: '/error-detection', label: 'Error Detection', icon: <FaExclamationTriangle /> },
    { section: TL, path: '/route-intelligence', label: 'Route Intelligence', icon: <FaBrain /> },
    { section: TL, path: '/telegram-bi', label: 'Telegram BI', icon: <FaTelegram /> },
    { section: TL, path: '/edge-sync', label: 'Edge Sync', icon: <FaSyncAlt /> },
    { section: TL, path: '/weighbridge-compliance', label: 'Compliance', icon: <FaBalanceScale /> },

    // ST-00200: guides, lessons and the glossary; every signed-in user (pagePermissions '/help': []).
    { section: HP, path: '/help', label: 'Help & training', icon: <FaQuestionCircle />, divider: true },

    // ST-00143: support tickets live on the lexadapt-support platform, embedded
    // same-origin at /support/ — a full-page link out of the SPA (external).
    {
      path: '/support',
      external: true,
      href: SUPPORT_APP_PATH,
      label: 'Support',
      icon: <FaTicketAlt />,
      badge: unreadTicketCount,
    },
    { path: '/settings', label: 'Settings', icon: <FaCog /> },
  ];
}

/**
 * The path of the entry to highlight for `pathname`: the entry itself, else the one with the
 * longest path the page sits under (`/` only ever matches itself). Null when none matches.
 */
export function activeMenuPath(items: readonly SidebarItem[], pathname: string): string | null {
  let best: string | null = null;
  for (const item of items) {
    if (item.external) continue;
    if (pathname === item.path) return item.path;
    if (
      item.path !== '/' &&
      pathname.startsWith(`${item.path}/`) &&
      (best === null || item.path.length > best.length)
    ) {
      best = item.path;
    }
  }
  return best;
}

const Tooltip = React.memo(({ label, y }: { label: string; y: number | null }) => {
  if (!y) return null;
  return ReactDOM.createPortal(
    <div className="sidebar-tooltip" style={{ top: `${y}px` }}>
      <span className="sidebar-tooltip-arrow" />
      {label}
    </div>,
    document.body
  );
});

// Hover hook used by collapsed-state icon buttons to position the portal tooltip
const useHoverTooltip = <T extends HTMLElement>(enabled: boolean) => {
  const [hoverY, setHoverY] = useState<number | null>(null);
  const ref = useRef<T>(null);

  const onMouseEnter = useCallback(() => {
    if (enabled && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHoverY(rect.top + rect.height / 2);
    }
  }, [enabled]);

  const onMouseLeave = useCallback(() => setHoverY(null), []);

  return { ref, hoverY, onMouseEnter, onMouseLeave };
};

// Individual menu item with tooltip support
interface MenuItemProps {
  item: SidebarItem;
  isActive: boolean;
  isCollapsed: boolean;
  isMobile: boolean;
  onNavigate?: () => void;
}

const MenuItem = React.memo(({ item, isActive, isCollapsed, isMobile, onNavigate }: MenuItemProps) => {
  const [hoverY, setHoverY] = useState<number | null>(null);
  const ref = useRef<HTMLLIElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (ref.current && isCollapsed && !isMobile) {
      const rect = ref.current.getBoundingClientRect();
      setHoverY(rect.top + rect.height / 2);
    }
  }, [isCollapsed, isMobile]);

  const handleMouseLeave = useCallback(() => {
    setHoverY(null);
  }, []);

  const handleClick = useCallback(() => {
    if (isMobile && onNavigate) onNavigate();
  }, [isMobile, onNavigate]);

  // External items (e.g. the embedded support platform at /support/#/…) leave
  // the SPA entirely, so they render a real full-page-navigation anchor rather
  // than an AppLink into a router/tab.
  const LinkTag: ElementType = item.external ? 'a' : AppLink;
  const linkTarget = item.external ? { href: item.href || item.path } : { to: item.path };
  const badge = item.badge ?? 0;

  return (
    <li ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <LinkTag
        {...linkTarget}
        onClick={handleClick}
        className={`sidebar-nav-link${isActive ? ' active' : ''}${isCollapsed ? ' collapsed' : ''}`}
        aria-current={isActive ? 'page' : undefined}
      >
        {isActive && <span className="sidebar-active-indicator" />}
        <span className={`sidebar-nav-icon${isActive ? ' active' : ''}`}>{item.icon}</span>
        {!isCollapsed && (
          <span
            className="sidebar-nav-label"
            style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}
          >
            {item.label}
            {badge > 0 && (
              <span
                style={{
                  background: '#0f766e',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '0 5px',
                  borderRadius: 10,
                  minWidth: 16,
                  textAlign: 'center',
                  lineHeight: '16px',
                }}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </span>
        )}
        {isCollapsed && <span className="sr-only">{item.label}</span>}
        {isCollapsed && badge > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              background: '#0f766e',
              width: 8,
              height: 8,
              borderRadius: '50%',
            }}
          />
        )}
      </LinkTag>
      {isCollapsed && !isMobile && hoverY && <Tooltip label={item.label} y={hoverY} />}
    </li>
  );
});

const EnhancedSidebar = React.memo(
  ({
    isCollapsed: controlledCollapsed,
    onToggle,
    defaultCollapsed = false,
    className = '',
    activePathname = '/',
  }: EnhancedSidebarProps) => {
    // `tenantLogo` is supplied by AuthContext as { dataUri, name } | null. It may
    // also be undefined (older context build) — every use below is optional.
    const { user, logout, authenticated, authEnabled, tenant, tenantLogo, hasPermission } =
      useAuth();
    const { mode: themeMode, setMode: setThemeMode } = useTheme();
    const sidebarRef = useRef<HTMLElement>(null);
    const isMobile = useIsMobile();

    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

    // Hover tooltips for collapsed-state icon buttons (toggle + sign-out)
    const collapsedTooltipsEnabled = isCollapsed && !isMobile;
    const toggleHover = useHoverTooltip<HTMLButtonElement>(collapsedTooltipsEnabled);
    const signoutHover = useHoverTooltip<HTMLButtonElement>(collapsedTooltipsEnabled);
    const themeHover = useHoverTooltip<HTMLButtonElement>(collapsedTooltipsEnabled);
    const toggleLabel = isCollapsed ? 'Expand sidebar' : 'Collapse sidebar';

    const themeMeta = THEME_META[themeMode] || THEME_META.detect;
    const ThemeIcon = themeMeta.icon;
    const themeLabel = `Theme: ${themeMeta.label}`;
    const cycleTheme = useCallback(
      () => setThemeMode(THEME_CYCLE[themeMode] || 'detect'),
      [themeMode, setThemeMode]
    );

    // Unread badge from the lexadapt-support platform. A background poll: it
    // pauses in hidden tabs and never refreshes the session (ST-00215 review S5).
    const unreadTicketCount: number = useSupportUnreadCount();
    // ST-00251: LexiStar's unread notifications (the bell loads and keeps the count).
    const unreadNotificationCount: number = useLexiStarUnread();

    const menuItems = useMemo<SidebarItem[]>(
      () => buildMenuItems(unreadTicketCount, unreadNotificationCount),
      [unreadTicketCount, unreadNotificationCount]
    );

    // Show only the pages this user is permitted to see. Permission requirements
    // come from the central pagePermissions map; pages with no mapped permission
    // (e.g. the home overview) are visible to any authenticated user. Admin-tier
    // users have ['*'] so hasPermission() returns true for everything.
    const visibleMenuItems = useMemo(
      () =>
        menuItems.filter((item) => {
          return permits(getRequiredPermission(item.path), hasPermission);
        }),
      [menuItems, hasPermission]
    );

    // One entry is highlighted: the most specific one the current page is on or under, so a
    // page under /lexistar/billing lights Usage & billing, and any other settings page Settings.
    const activeItemPath = useMemo(
      () => activeMenuPath(visibleMenuItems, activePathname),
      [visibleMenuItems, activePathname]
    );

    const handleToggle = useCallback(() => {
      if (controlledCollapsed !== undefined && onToggle) {
        onToggle();
      } else {
        setInternalCollapsed((prev) => !prev);
      }
    }, [controlledCollapsed, onToggle]);

    // Close on outside click (mobile only)
    useEffect(() => {
      if (!isMobile || isCollapsed) return;

      const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
        if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
          handleToggle();
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }, [isMobile, isCollapsed, handleToggle]);

    // Escape key closes sidebar
    useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isCollapsed) handleToggle();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [isCollapsed, handleToggle]);

    return (
      <>
        {/* Mobile backdrop */}
        {isMobile && !isCollapsed && (
          <div className="sidebar-backdrop" onClick={handleToggle} aria-hidden="true" />
        )}

        <aside
          ref={sidebarRef}
          className={`enhanced-sidebar ${isMobile ? 'mobile' : 'desktop'} ${isCollapsed ? 'collapsed' : 'expanded'} ${className}`}
          role="navigation"
          aria-label="Main navigation"
          data-expanded={!isCollapsed}
        >
          {/* Header */}
          <div className="sidebar-header">
            {!isCollapsed ? (
              <>
                <div className="sidebar-brand">
                  <div className="sidebar-logo-wide" role="img" aria-label="LexiBridge">
                    <span className="sidebar-logo-wide-lexi">Lexi</span>
                    <span className="sidebar-logo-wide-divider" />
                    <span className="sidebar-logo-wide-bridge">Bridge</span>
                  </div>
                  {/* Tenant logo — secondary to the LexiBridge wordmark above it.
                    Only rendered when the tenant actually has one. */}
                  {tenantLogo?.dataUri && (
                    <img
                      src={tenantLogo.dataUri}
                      alt={tenantLogo.name || tenant?.tenant_name || 'Organization logo'}
                      className="sidebar-tenant-logo"
                    />
                  )}
                  {tenant?.tenant_name && (
                    <p className="sidebar-tenant" title={tenant.tenant_name}>
                      {tenant.tenant_name}
                    </p>
                  )}
                </div>
                <div className="sidebar-logo-icon-wrap">
                  <img src="/icon-source.svg" alt="" className="sidebar-logo-icon" />
                </div>
              </>
            ) : (
              <div className="sidebar-logo-sm-wrap">
                <img src="/icon-source.svg" alt="LexiBridge" className="sidebar-logo-sm-icon" />
              </div>
            )}

            {isMobile && !isCollapsed && (
              <button
                onClick={handleToggle}
                className="sidebar-close-btn"
                aria-label="Close navigation"
              >
                <FaTimes size={16} />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav" id="sidebar-navigation" aria-orientation="vertical">
            <ul className="sidebar-nav-list">
              {visibleMenuItems.map((item, index) => (
                <React.Fragment key={item.path}>
                  {item.section && item.section !== visibleMenuItems[index - 1]?.section &&
                    (isCollapsed ? (
                      index > 0 && <li className="sidebar-divider" aria-hidden="true" />
                    ) : (
                      <li className="sidebar-section-title" role="presentation">
                        {item.section}
                      </li>
                    ))}
                  <MenuItem
                    item={item}
                    isActive={item.path === activeItemPath}
                    isCollapsed={isCollapsed}
                    isMobile={isMobile}
                    onNavigate={handleToggle}
                  />
                  {item.divider && <li className="sidebar-divider" aria-hidden="true" />}
                </React.Fragment>
              ))}
            </ul>
          </nav>

          {/* Theme cycle — Detect → Light → Dark. Available collapsed and expanded,
            desktop and mobile. The sidebar itself stays dark in both themes. */}
          <div className="sidebar-theme-area">
            <button
              ref={themeHover.ref}
              onClick={cycleTheme}
              onMouseEnter={themeHover.onMouseEnter}
              onMouseLeave={themeHover.onMouseLeave}
              className={`sidebar-theme-btn${isCollapsed ? ' collapsed' : ''}`}
              title={themeLabel}
              aria-label={themeLabel}
              type="button"
            >
              <ThemeIcon size={16} strokeWidth={2} className="sidebar-theme-icon" />
              {!isCollapsed && (
                <>
                  <span className="sidebar-theme-text">Theme</span>
                  <span className="sidebar-theme-value">{themeMeta.label}</span>
                </>
              )}
            </button>
            {themeHover.hoverY && <Tooltip label={themeLabel} y={themeHover.hoverY} />}
          </div>

          {/* Toggle — desktop only */}
          {!isMobile && (
            <div className="sidebar-toggle-area">
              <button
                ref={toggleHover.ref}
                onClick={handleToggle}
                onMouseEnter={toggleHover.onMouseEnter}
                onMouseLeave={toggleHover.onMouseLeave}
                className="sidebar-toggle-btn"
                aria-label={toggleLabel}
                aria-expanded={!isCollapsed}
                aria-controls="sidebar-navigation"
                title={toggleLabel}
                type="button"
              >
                {isCollapsed ? <FaAngleDoubleRight size={16} /> : <FaAngleDoubleLeft size={16} />}
                {!isCollapsed && <span className="sidebar-toggle-text">Collapse</span>}
              </button>
              {toggleHover.hoverY && <Tooltip label={toggleLabel} y={toggleHover.hoverY} />}
            </div>
          )}

          {/* User section */}
          {authEnabled && authenticated && (
            <div className="sidebar-user-section">
              {!isCollapsed ? (
                <>
                  <div className="sidebar-user-info">
                    <div className="sidebar-user-avatar">
                      {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="sidebar-user-details">
                      <p className="sidebar-user-name">{user?.username || user?.email}</p>
                      {user?.email && user?.username && (
                        <p className="sidebar-user-email">{user.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    className="sidebar-signout-btn"
                    title="Sign out of your account"
                    type="button"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <button
                    ref={signoutHover.ref}
                    onClick={logout}
                    onMouseEnter={signoutHover.onMouseEnter}
                    onMouseLeave={signoutHover.onMouseLeave}
                    className="sidebar-signout-icon"
                    title="Sign out of your account"
                    aria-label="Sign out"
                    type="button"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </button>
                  {signoutHover.hoverY && <Tooltip label="Sign out" y={signoutHover.hoverY} />}
                </>
              )}
            </div>
          )}

          {/* Footer */}
          {!isCollapsed && (
            <div className="sidebar-footer">
              <p>&copy; 2025 Lex-Adapt (Pty) Ltd</p>
            </div>
          )}

          {/* Screen reader announcements */}
          <div className="sr-only" aria-live="polite" aria-atomic="true" role="status">
            {isCollapsed ? 'Sidebar collapsed' : 'Sidebar expanded'}
          </div>
        </aside>
      </>
    );
  }
);

EnhancedSidebar.displayName = 'EnhancedSidebar';

export default EnhancedSidebar;
